/**
 * Sprint 23 — StatusPill.
 *
 * The live status indicator on every AgentCard (and, from 23.6, the agent
 * profile). A coloured dot + a themed label, with the freeform verb for the
 * working state and a duration suffix. Colours map to the workforce tiers:
 *   working   → active  (teal, pulsing)
 *   awaiting  → scheduled (amber)
 *   available → idle (gray)
 */

import type { CSSProperties } from "react";
import type { WorkforceStatusKind } from "../../../lib/types";
import type { ThemedLabels } from "../../lib/themed-labels";

const DOT_COLOR: Record<WorkforceStatusKind, string> = {
  working: "var(--kuvio-status-active)",
  awaiting: "var(--kuvio-status-scheduled)",
  available: "var(--kuvio-status-idle)",
};

export interface StatusPillProps {
  status: WorkforceStatusKind;
  verb: string | null;
  durationSeconds: number | null;
  labels: ThemedLabels;
}

/** Compact human duration: "just now", "14m", "2h", "3d". */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function StatusPill({ status, verb, durationSeconds, labels }: StatusPillProps) {
  const primary =
    status === "working"
      ? verb || labels.statusWorking
      : status === "awaiting"
        ? labels.statusAwaiting
        : labels.statusAvailable;

  const dur = formatDuration(durationSeconds);
  // Available shows "last active 3d ago"; working/awaiting show elapsed-in-state.
  const durationText = dur ? (status === "available" ? `${dur} ago` : dur) : null;

  const wrap: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "var(--font-sans)",
    fontSize: 12,
    minWidth: 0,
  };

  const dot: CSSProperties = {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: DOT_COLOR[status],
    flexShrink: 0,
  };

  const ariaLabel = `${primary}${durationText ? `, ${durationText}` : ""}`;

  return (
    <span style={wrap} aria-label={ariaLabel}>
      <span
        style={dot}
        className={status === "working" ? "kuvio-status-active-pulse" : undefined}
        aria-hidden="true"
      />
      <span
        style={{
          color: "var(--color-text-secondary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={primary}
      >
        {primary}
      </span>
      {durationText ? (
        <span style={{ color: "var(--color-text-tertiary)", flexShrink: 0 }}>· {durationText}</span>
      ) : null}
    </span>
  );
}
